import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { DatePickerWithYearMonth } from '@/components/DatePickerWithYearMonth';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { 
  Search, CalendarIcon, FileText, Download, Filter, 
  X, Newspaper, ChevronRight 
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api, { getApiBaseUrl } from '@/lib/api';

// Função para remover tags HTML do texto
const stripHtml = (html) => {
  if (!html) return '';
  // Criar um elemento temporário para extrair texto
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

const CATEGORIAS = ["Executivo", "Legislativo", "Terceiro Setor"];
const SUBCATEGORIAS = {
  "Executivo": ["Leis", "Decretos", "Portarias", "Resoluções", "Extratos", "Editais", "Avisos de Licitação", "Designações", "Concursos Públicos", "Prestação de Contas"],
  "Legislativo": ["Leis", "Decretos", "Resoluções", "Portarias", "Editais", "Atas", "Pareceres"],
  "Terceiro Setor": ["Editais", "Extratos", "Prestação de Contas", "Convênios", "Termos de Parceria"]
};

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [results, setResults] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [anos, setAnos] = React.useState([]);
  
  // Filters
  const [termo, setTermo] = React.useState(searchParams.get('termo') || '');
  const [categoria, setCategoria] = React.useState(searchParams.get('categoria') || '');
  const [subcategoria, setSubcategoria] = React.useState(searchParams.get('subcategoria') || '');
  const [ano, setAno] = React.useState(searchParams.get('ano') || '');
  const [dataInicio, setDataInicio] = React.useState(null);
  const [dataFim, setDataFim] = React.useState(null);

  React.useEffect(() => {
    fetchAnos();
    // Auto search if params exist
    if (searchParams.toString()) {
      handleSearch();
    }
  }, []);

  const fetchAnos = async () => {
    try {
      const response = await api.get('/edicoes/anos');
      setAnos(response.data.anos);
    } catch (error) {
      console.error('Error fetching anos:', error);
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      const params = {};
      if (termo) params.termo = termo;
      if (categoria) params.categoria = categoria;
      if (subcategoria) params.subcategoria = subcategoria;
      if (ano) params.ano = parseInt(ano);
      if (dataInicio) params.data_inicio = format(dataInicio, 'yyyy-MM-dd');
      if (dataFim) params.data_fim = format(dataFim, 'yyyy-MM-dd');

      const response = await api.get('/buscar', { params });
      setResults(response.data);

      // Update URL params
      const newParams = new URLSearchParams();
      if (termo) newParams.set('termo', termo);
      if (categoria) newParams.set('categoria', categoria);
      if (subcategoria) newParams.set('subcategoria', subcategoria);
      if (ano) newParams.set('ano', ano);
      setSearchParams(newParams);
    } catch (error) {
      console.error('Error searching:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setTermo('');
    setCategoria('');
    setSubcategoria('');
    setAno('');
    setDataInicio(null);
    setDataFim(null);
    setResults([]);
    setSearchParams({});
  };

  const getCategoryBadgeClass = (cat) => {
    switch (cat) {
      case 'Executivo': return 'bg-[#B91C1C] text-white';
      case 'Legislativo': return 'bg-[#1D4ED8] text-white';
      case 'Terceiro Setor': return 'bg-[#059669] text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), "dd/MM/yyyy", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  const availableSubcategorias = categoria ? SUBCATEGORIAS[categoria] || [] : [];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="search-page">
        <h1 className="font-heading text-3xl font-bold mb-8">Pesquisa Avançada</h1>

        {/* Search Filters */}
        <Card className="mb-8">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              {/* Search Term */}
              <div className="lg:col-span-2">
                <Label htmlFor="termo">Termo de busca</Label>
                <div className="relative mt-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    id="termo"
                    placeholder="Digite palavras-chave..."
                    value={termo}
                    onChange={(e) => setTermo(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="pl-10"
                    data-testid="search-input"
                  />
                </div>
              </div>

              {/* Categoria */}
              <div>
                <Label>Categoria</Label>
                <Select value={categoria || "all"} onValueChange={(v) => { setCategoria(v === "all" ? "" : v); setSubcategoria(''); }}>
                  <SelectTrigger className="mt-1" data-testid="categoria-select">
                    <SelectValue placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {CATEGORIAS.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Subcategoria */}
              <div>
                <Label>Tipo</Label>
                <Select value={subcategoria || "all"} onValueChange={(v) => setSubcategoria(v === "all" ? "" : v)} disabled={!categoria}>
                  <SelectTrigger className="mt-1" data-testid="subcategoria-select">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {availableSubcategorias.map((sub) => (
                      <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Ano */}
              <div>
                <Label>Ano</Label>
                <Select value={ano || "all"} onValueChange={(v) => setAno(v === "all" ? "" : v)}>
                  <SelectTrigger className="mt-1" data-testid="ano-select">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {anos.map((a) => (
                      <SelectItem key={a} value={String(a)}>{a}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Data Início */}
              <div>
                <Label>Data Início</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full mt-1 justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dataInicio ? format(dataInicio, "dd/MM/yyyy") : "Selecionar"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <DatePickerWithYearMonth
                      selected={dataInicio}
                      onSelect={setDataInicio}
                      locale={ptBR}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Data Fim */}
              <div>
                <Label>Data Fim</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full mt-1 justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dataFim ? format(dataFim, "dd/MM/yyyy") : "Selecionar"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <DatePickerWithYearMonth
                      selected={dataFim}
                      onSelect={setDataFim}
                      locale={ptBR}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3 pt-4 border-t">
              <Button 
                onClick={handleSearch} 
                className="bg-[#B91C1C] hover:bg-[#991B1B]"
                disabled={loading}
                data-testid="search-btn"
              >
                <Search size={18} className="mr-2" />
                {loading ? 'Buscando...' : 'Buscar'}
              </Button>
              <Button variant="outline" onClick={clearFilters}>
                <X size={18} className="mr-2" />
                Limpar Filtros
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-xl font-semibold">
              Resultados {results.length > 0 && `(${results.length})`}
            </h2>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : results.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-gray-500">
                <Newspaper className="mx-auto mb-4 text-gray-300" size={48} />
                <p>Nenhum resultado encontrado</p>
                <p className="text-sm mt-2">Tente ajustar os filtros de busca</p>
              </CardContent>
            </Card>
          ) : (
            results.map((pub, index) => (
              <Card key={`${pub.publicacao_id}-${index}`} className="hover:shadow-md transition-shadow pub-card" data-testid={`result-${index}`}>
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <Badge className={getCategoryBadgeClass(pub.categoria)}>
                          {pub.categoria}
                        </Badge>
                        <Badge variant="outline">{pub.subcategoria}</Badge>
                        <span className="text-sm text-gray-500">
                          Edição {pub.numero_edicao}/{pub.ano}
                        </span>
                      </div>
                      
                      <h3 className="font-heading font-bold text-lg mb-2">{pub.titulo}</h3>
                      
                      <p className="text-gray-600 dark:text-gray-400 text-sm mb-3 line-clamp-3">
                        {stripHtml(pub.texto)}
                      </p>
                      
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                        <span>{formatDate(pub.data_publicacao)}</span>
                        <span className="text-xs bg-gray-100 px-2 py-1 rounded font-mono">
                          {pub.codigo_verificacao}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex gap-2 shrink-0">
                      <Link to={`/edicao/${pub.edicao_id}`}>
                        <Button variant="outline" size="sm">
                          <FileText size={14} className="mr-1" />
                          Ver Edição
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </Layout>
  );
}
